import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, Btn, Input } from "@/components/erp/ui";
import { MediaInput } from "@/components/erp/prod/ProdInputs";
import { useFlow, WoHead, Denied, err, uploadFlow } from "@/components/erp/flow/FlowCommon";
import { MessageCircle, Mail, Copy } from "lucide-react";
import { SocialPlan } from "@/components/erp/social/SocialPlan";
import { SocialKpi } from "@/components/erp/social/SocialKpi";
import { SocialTrends } from "@/components/erp/social/SocialTrends";
import { SocialAds } from "@/components/erp/social/SocialAds";
import { AdsReport } from "@/components/erp/social/AdsReport";
import { TeamPrPanel } from "@/components/erp/purchasing/TeamPrPanel";

const ST = { DRAFT: "Draft", SENT: "Terkirim ke customer — menunggu jawaban", REWORK: "Sedang diperbaiki (komplain)", APPROVED: "Disetujui" };

const Complaint = ({ w, load, close }) => {
  const [note, setNote] = useState(""); const [sel, setSel] = useState([]);
  const send = async () => { try { await api.post(`/flow/${w.wo_id}/docs/response`, { ok: false, note, reopen: sel }); toast.success("Komplain diteruskan & tahap dibuka lagi"); close(); load(); } catch (e) { err(e); } };
  return (
    <div className="mt-3 rounded-xl border border-rose-200 bg-rose-50 p-3" data-testid="docs-complaint">
      <Input placeholder="Keluhan / ketidaksesuaian dari customer" data-testid="docs-complaint-note" value={note} onChange={(e) => setNote(e.target.value)} />
      <div className="flex flex-wrap gap-2 mt-2 text-sm">Buka lagi:
        {[["DESIGN", "Design"], ["PRINTING", "Produksi"], ["FINISHING", "Finishing"]].map(([k, l]) => (
          <button key={k} type="button" data-testid={`docs-reopen-${k}`} onClick={() => setSel(sel.includes(k) ? sel.filter((x) => x !== k) : [...sel, k])}
            className={`px-3 py-1 rounded-full border text-xs ${sel.includes(k) ? "bg-rose-600 text-white border-rose-600" : "border-slate-300 bg-white"}`}>{l}</button>
        ))}
      </div>
      <p className="text-[11px] text-slate-500 mt-1">Semua tindakan perbaikan dihitung sebagai biaya tambahan WO dan memengaruhi bonus project.</p>
      <Btn className="mt-2" data-testid="docs-complaint-send" onClick={send}>Teruskan ke tim</Btn>
    </div>
  );
};

const SendBar = ({ w, ph, vd, load }) => {
  const [msg, setMsg] = useState(null);
  const ready = ph.length >= 3 && vd.length >= 1;
  const prep = async () => { await api.put(`/flow/${w.wo_id}/docs`, { photos: ph, videos: vd }); const { data } = await api.post(`/flow/${w.wo_id}/docs/send`); setMsg(data); load(); return data; };
  const wa = async () => {
    const win = window.open("about:blank", "_blank");
    try { const d = await prep(); if (win) win.location.href = d.wa_url; else window.location.href = d.wa_url; if (!d.phone) toast.warning("No. WA customer kosong — pilih kontak di WhatsApp"); }
    catch (e) { win?.close(); err(e); }
  };
  const mail = async () => { try { const d = await prep(); if (!d.email) toast.warning("Email customer kosong — isi tujuan di aplikasi email"); window.location.href = d.mail_url; } catch (e) { err(e); } };
  const copy = async () => {
    try {
      const d = await prep();
      try { await navigator.clipboard.writeText(d.message); toast.success("Pesan & link galeri disalin — tempel di chat / email customer"); }
      catch { toast.info("Salin otomatis tidak didukung — salin pesan di kotak bawah"); }
    } catch (e) { err(e); }
  };
  return (
    <div className="w-full">
      <div className="flex flex-wrap gap-2">
        <Btn data-testid={`docs-wa-${w.wo_code}`} disabled={!ready} onClick={wa}><MessageCircle className="w-4 h-4" /> Kirim via WA</Btn>
        <Btn variant="ghost" data-testid={`docs-mail-${w.wo_code}`} disabled={!ready} onClick={mail}><Mail className="w-4 h-4" /> Kirim via email</Btn>
        <Btn variant="ghost" data-testid={`docs-copy-${w.wo_code}`} disabled={!ready} onClick={copy}><Copy className="w-4 h-4" /> Salin pesan (manual)</Btn>
      </div>
      {!ready && <p className="text-[11px] text-amber-700 mt-1" data-testid={`docs-need-${w.wo_code}`}>Tombol kirim aktif setelah ada minimal 3 foto & 1 video (sekarang {ph.length} foto, {vd.length} video).</p>}
      {msg && <div className="mt-2 rounded-lg bg-slate-50 border border-slate-200 p-2 text-xs select-all" data-testid={`docs-msg-${w.wo_code}`}><div className="whitespace-pre-wrap">{msg.message}</div><a href={msg.link} target="_blank" rel="noreferrer" className="text-sky-700 underline">{msg.link}</a></div>}
    </div>
  );
};

const DocsCard = ({ w, load }) => {
  const [ph, setPh] = useState(w.docs.photos || []); const [vd, setVd] = useState(w.docs.videos || []); const [cmp, setCmp] = useState(false);
  const save = async () => { try { await api.put(`/flow/${w.wo_id}/docs`, { photos: ph, videos: vd }); toast.success("Dokumentasi disimpan"); load(); } catch (e) { err(e); } };
  const ok = async () => { try { await api.post(`/flow/${w.wo_id}/docs/response`, { ok: true }); toast.success("Disetujui — lanjut packing"); load(); } catch (e) { err(e); } };
  return (
    <div>
      <div className="text-xs mb-2" data-testid={`docs-status-${w.wo_code}`}>Status: <b>{ST[w.docs.status] || "Belum ada dokumentasi"}</b>{w.docs.sent_at ? ` · dikirim ${w.docs.sent_at.slice(0, 16).replace("T", " ")}` : ""}</div>
      <div className="grid sm:grid-cols-2 gap-3">
        <MediaInput value={ph} onChange={setPh} max={4} uploader={uploadFlow} testid={`docs-photos-${w.wo_code}`} label={`Foto produk (3–4) · ${ph.length}/4`} />
        <MediaInput value={vd} onChange={setVd} max={3} allowVideo videoOnly maxSec={600} uploader={uploadFlow} testid={`docs-videos-${w.wo_code}`} label={`Video (1–3) · ${vd.length}/3`} />
      </div>
      {w.complaints.length > 0 && <div className="text-[11px] text-rose-700 mt-2">{w.complaints.map((c, i) => <div key={i}>Komplain {c.at.slice(0, 10)}: {c.note} → {c.reopen.join(", ")}</div>)}</div>}
      <div className="flex flex-wrap gap-2 mt-3">
        <Btn variant="ghost" data-testid={`docs-save-${w.wo_code}`} onClick={save}>Simpan</Btn>
        <SendBar w={w} ph={ph} vd={vd} load={load} />
        {w.docs.status === "SENT" && <><Btn data-testid={`docs-ok-${w.wo_code}`} onClick={ok}>Customer OK → packing</Btn><Btn variant="ghost" data-testid={`docs-issue-${w.wo_code}`} onClick={() => setCmp(!cmp)}>Ada ketidaksesuaian</Btn></>}
      </div>
      {cmp && <Complaint w={w} load={load} close={() => setCmp(false)} />}
    </div>
  );
};

const WaSettings = ({ d, load }) => {
  const [t, setT] = useState(d.settings.wa_template);
  if (!d.is_owner) return null;
  const save = async () => { try { await api.put("/flow/settings", { wa_template: t }); toast.success("Template WA disimpan"); load(); } catch (e) { err(e); } };
  return (
    <details className="rounded-2xl border border-slate-200 bg-white p-4 mt-6" data-testid="docs-wa-settings">
      <summary className="font-semibold cursor-pointer">Pengaturan pesan WhatsApp (owner)</summary>
      <textarea className="w-full border border-slate-300 rounded-lg p-2 text-sm mt-2" rows={5} data-testid="docs-wa-template" value={t} onChange={(e) => setT(e.target.value)} />
      <p className="text-[11px] text-slate-500">Kode otomatis: {"{customer} {product} {wo} {link}"} — {"{link}"} wajib (link galeri foto & video).</p>
      <Btn className="mt-2" data-testid="docs-wa-save" onClick={save}>Simpan</Btn>
    </details>
  );
};

const AdsTab = () => {
  const [rev, setRev] = useState(0);
  return <><SocialAds key={rev} /><AdsReport key={`r${rev}`} /><div className="mt-4"><TeamPrPanel onSaved={() => setRev((r) => r + 1)} /></div></>;
};

export default function DocsDashboard() {
  const { d, load, denied } = useFlow("DOCS");
  const [tab, setTab] = useState("ads");
  if (denied) return <Denied />;
  if (!d) return <div className="p-6 text-sm text-slate-400">Memuat…</div>;
  return (
    <div data-testid="docs-page">
      <PageHeader title="Dashboard Social Media & Dokumentasi" subtitle="Dokumentasi unit → WA customer · jadwal posting · performa KPI · iklan · tren AI" />
      <div className="flex flex-wrap gap-2 mb-5" data-testid="docs-tabs">
        {[["ads", "Jadwal ADS"], ["docs", `Dokumentasi (${d.wos.length})`], ["plan", "Jadwal Posting"], ["kpi", "Performa KPI"], ["trend", "Tren AI"]].map(([k, l]) => (
          <button key={k} type="button" data-testid={`docs-tab-${k}`} onClick={() => setTab(k)}
            className={`px-4 py-2 rounded-full text-sm border transition-colors ${tab === k ? "bg-slate-900 text-white border-slate-900" : "border-slate-300 bg-white"}`}>{l}</button>
        ))}
      </div>
      {tab === "ads" && <AdsTab />}
      {tab === "plan" && <SocialPlan />}
      {tab === "kpi" && <SocialKpi />}
      {tab === "trend" && <SocialTrends />}
      {tab === "docs" && <>
      <div className="space-y-3">{d.wos.map((w) => <WoHead key={w.wo_id} w={w}><DocsCard key={w.wo_id} w={w} load={load} /></WoHead>)}</div>
      {!d.wos.length && <p className="text-sm text-slate-400" data-testid="docs-empty">Belum ada unit yang siap didokumentasikan.</p>}
      <WaSettings d={d} load={load} />
      </>}
    </div>
  );
}
